import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useState, type CSSProperties } from "react";

import { cn } from "../../lib/utils";

/**
 * Clerk serves its hosted images through a resizing CDN: the thumbnail params
 * ride the query string (`?width=88&height=88&fit=crop&quality=85`). Quality is
 * optional (the detail header omits it).
 */
export function sizedPhotoUrl(url: string, size: number, quality?: number) {
  const params = new URLSearchParams({
    width: String(size),
    height: String(size),
    fit: "crop",
  });
  if (quality) params.set("quality", String(quality));
  return `${url}${url.includes("?") ? "&" : "?"}${params.toString()}`;
}

/** Up to two initials from the first two words of `name`, uppercased. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => Array.from(word)[0])
    .join("")
    .toUpperCase();
}

/**
 * An agent's headshot, or their initials when there is none (absent URL, or
 * the image fails to load). The photo is the pro's Clerk-hosted image carried
 * as `identity.photo_url`; this component only formats it. With `enlargeable`
 * the photo (never the initials) is a button that opens a lightbox showing the
 * bare, untransformed URL.
 */
export function AgentAvatar({
  name,
  photoUrl,
  imageSize,
  quality,
  className,
  fallbackClassName,
  fallbackStyle,
  enlargeable = false,
}: {
  name: string;
  photoUrl?: string | null;
  /** Requested thumbnail edge in px (the rendered box is smaller: 2x+ density). */
  imageSize: number;
  quality?: number;
  /** Shared by the photo and the fallback: size, shape, shrink behavior. */
  className?: string;
  /** Fallback-only: background, type. */
  fallbackClassName?: string;
  fallbackStyle?: CSSProperties;
  enlargeable?: boolean;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const photo = photoUrl && photoUrl !== failedUrl ? photoUrl : null;

  if (!photo) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "flex items-center justify-center",
          className,
          fallbackClassName,
        )}
        data-testid="agent-initials"
        style={fallbackStyle}
      >
        {initialsOf(name)}
      </span>
    );
  }

  const img = (
    <img
      alt=""
      className={cn("object-cover", className)}
      data-testid="agent-photo"
      onError={() => setFailedUrl(photo)}
      src={sizedPhotoUrl(photo, imageSize, quality)}
    />
  );
  if (!enlargeable) return img;

  return (
    <DialogPrimitive.Root onOpenChange={setOpen} open={open}>
      <DialogPrimitive.Trigger
        aria-label={`View ${name}'s photo`}
        className="shrink-0 cursor-zoom-in rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        data-slot="interactive-control"
        type="button"
      >
        {img}
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          data-testid="photo-lightbox-backdrop"
          onClick={() => setOpen(false)}
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 outline-none"
        >
          <DialogPrimitive.Title className="sr-only">
            {`${name}'s photo`}
          </DialogPrimitive.Title>
          <img
            alt={`${name}'s photo`}
            className="max-h-[80dvh] max-w-[min(90vw,32rem)] rounded-2xl object-contain shadow-xl"
            src={photo}
          />
          <DialogPrimitive.Close
            aria-label="Close photo"
            className="absolute -right-3 -top-3 rounded-full bg-card p-2 text-foreground shadow-md hover:bg-muted"
            data-slot="interactive-control"
          >
            <X aria-hidden="true" className="size-4" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
